import { ErrorMessage } from '@hookform/error-message';
import { yupResolver } from '@hookform/resolvers/yup';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Autocomplete, Box, Button, Divider, Stack, TextField, Typography } from '@mui/material';
import { RootDialog, RootDialogTitle } from 'components/RootDialog';
import _ from 'lodash';
import { FC, useCallback, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { Error } from '../../../../../../../components/Common/Error';
import MandatoryFormFieldLabel from '../../../../../../../components/MandatoryFormFieldLabel';
import { MenuItem } from '../../../../../../../components/styled/SelectMenuItem';

import { APP_STRUCTURE } from '@constants/ACL/app_structure';
import { useFeedback } from '../../../../../../../hooks/useFeedback';
import { usePermissionsManagerContext } from '../context';
import { FormActionTypeEnum, PermissionFormModel } from '../models';

const schema = yup.object().shape({
  code: yup.string().required('Please enter name field'),
  name: yup.string().required('Please enter desciption field'),
  dependenciesApiPermissions: yup
    .array()
    .nullable()
    .transform(value => (value === null ? [] : value))
    .of(yup.string().trim().required('Each dependency must be filled')),
});

const DialogForm: FC<{
  actionType: FormActionTypeEnum;
  open: boolean;
  handleClose: VoidFunction;
  defaultValues: PermissionFormModel;
}> = ({ actionType, open, handleClose, defaultValues }) => {
  return (
    <RootDialog
      maxWidth={'lg'}
      onClose={() => handleClose()}
      open={open}
      children={
        <Box sx={{ minWidth: 400 }}>
          <RootDialogTitle component={'div'} onClose={() => handleClose()}>
            <Typography variant="inter14" fontWeight={'fontWeightMedium'} color="colors.gray.o50">
              ACL.Permissions
            </Typography>

            <Typography
              variant="inter20"
              fontWeight={'fontWeightSemibold'}
              color="common.black"
              sx={{ lineHeight: 'normal' }}
            >
              {actionType === FormActionTypeEnum.ADD ? 'Add Permission' : 'Update Permission'}
            </Typography>
          </RootDialogTitle>
          <Box>
            <PermissionForm
              actionType={actionType}
              defaultValues={defaultValues}
              handleClose={handleClose}
            />
          </Box>
        </Box>
      }
    />
  );
};

const PermissionForm: FC<{
  actionType: FormActionTypeEnum;
  defaultValues: PermissionFormModel;
  handleClose: VoidFunction;
}> = ({
  actionType,
  defaultValues = { parentPath: '', name: '', code: '', dependenciesApiPermissions: [] },
  handleClose,
}) => {
  const { data, createMutation, updateMutation } = usePermissionsManagerContext();
  const formCtx = useForm<PermissionFormModel>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues,
  });

  const { onError } = useFeedback();

  const mutation = actionType === FormActionTypeEnum.ADD ? createMutation : updateMutation;

  /**
   *
   */
  const onSubmit = (data: PermissionFormModel) => {
    mutation.mutate(data, {
      onError: (error: any) => {
        onError({
          snackbar: {
            title: 'Error',
            message:
              typeof error?.response?.data?.error === 'string'
                ? error?.response?.data?.error
                : 'An error occurred',
          },
        });
      },
    });
  };

  /**
   *
   */
  const codeOptions = useMemo(() => {
    const targetPath =
      actionType === FormActionTypeEnum.UPDATE
        ? defaultValues.parentPath.substring(0, defaultValues.parentPath.lastIndexOf('.'))
        : defaultValues.parentPath;

    const pathParts =
      targetPath !== '' ? targetPath.split('.').join('.children.').split('.') : null;

    const appStructureOptions = Object.keys(
      pathParts !== null ? _.get(APP_STRUCTURE, [...pathParts, 'children'], {}) : APP_STRUCTURE
    );

    const usedOptions = Object.keys(
      pathParts !== null ? _.get(data, [...pathParts, 'children'], {}) : data
    );

    const options = appStructureOptions.filter(x => !usedOptions.includes(x));

    return FormActionTypeEnum.UPDATE ? [...options, defaultValues.code] : options;
  }, [actionType, defaultValues.parentPath, data]);

  /**
   *
   */
  const getNameFromAppStructure = (path: string) => {
    const pathParts = path !== '' ? path.split('.').join('.children.').split('.') : null;

    return _.get(APP_STRUCTURE, [...(pathParts ?? []), 'name'], '');
  };

  return (
    <FormProvider {...formCtx}>
      <form onSubmit={formCtx.handleSubmit(onSubmit)} autoComplete="off" style={{ height: '100%' }}>
        <Stack direction={'column'} spacing={2}>
          <Typography variant="inter12" fontStyle={'italic'} color={'colors.gray.o50'}>
            <b>Target Path :</b> {defaultValues.parentPath}
          </Typography>

          <Controller
            name="code"
            control={formCtx.control}
            render={({ field, fieldState }) => {
              return (
                <Autocomplete
                  disableCloseOnSelect
                  disableClearable
                  disabledItemsFocusable
                  disabled={actionType === FormActionTypeEnum.UPDATE}
                  fullWidth
                  blurOnSelect
                  clearOnBlur
                  value={field.value}
                  getOptionLabel={option => {
                    const path =
                      defaultValues.parentPath === ''
                        ? option
                        : actionType === FormActionTypeEnum.UPDATE
                        ? `${defaultValues.parentPath.substring(
                            0,
                            defaultValues.parentPath.lastIndexOf('.')
                          )}.${option}`
                        : `${defaultValues.parentPath}.${option}`;
                    return String(getNameFromAppStructure(path));
                  }}
                  onChange={(e, newValue) => {
                    field.onChange(newValue);
                    const path =
                      defaultValues.parentPath === ''
                        ? newValue
                        : actionType === FormActionTypeEnum.UPDATE
                        ? `${defaultValues.parentPath.substring(
                            0,
                            defaultValues.parentPath.lastIndexOf('.')
                          )}.${newValue}`
                        : `${defaultValues.parentPath}.${newValue}`;
                    formCtx.setValue('name', getNameFromAppStructure(path));
                  }}
                  options={codeOptions || []}
                  renderOption={(props, option) => (
                    <MenuItem {...props} value={option}>
                      {option}
                    </MenuItem>
                  )}
                  renderInput={props => (
                    <TextField
                      name={field.name}
                      {...props}
                      variant="standard"
                      label={<MandatoryFormFieldLabel primaryLabel={'Code'} />}
                      className="background"
                      placeholder="Select Room"
                      error={fieldState.error !== undefined}
                      helperText={
                        <ErrorMessage
                          errors={formCtx.formState.errors}
                          name={field.name}
                          render={({ message }) => <Error>{message}</Error>}
                        />
                      }
                    />
                  )}
                />
              );
            }}
          />

          <Controller
            name="name"
            control={formCtx.control}
            render={({ field }) => (
              <Stack>
                <TextField
                  {...field}
                  className="background"
                  id={`${field.name}-input`}
                  label={<MandatoryFormFieldLabel primaryLabel={'Name'} />}
                  variant="standard"
                  error={formCtx.formState.errors[field.name] ? true : false}
                  margin="dense"
                  type="text"
                />
                <ErrorMessage
                  errors={formCtx.formState.errors}
                  name={field.name}
                  render={({ message }) => (
                    <Typography variant="inter12" color="error">
                      {message}
                    </Typography>
                  )}
                />
              </Stack>
            )}
          />

          <Controller
            name="dependenciesApiPermissions"
            control={formCtx.control}
            render={({ field }) => (
              <DependenciesApiPermissions
                items={field.value ?? []}
                onChange={value => field.onChange(value)}
              />
            )}
          />

          <Divider />

          <Box>
            <Stack direction={'row'} justifyContent={'space-between'}>
              <Button type="button" variant="outlined" size="large" onClick={() => handleClose()}>
                Cancel
              </Button>

              <Button type="submit" variant="contained" size="large" disabled={mutation.isLoading}>
                Save
              </Button>
            </Stack>
          </Box>
        </Stack>
      </form>
    </FormProvider>
  );
};

const DependenciesApiPermissions: FC<{
  items: Array<string>;
  onChange: (value: Array<string>) => void;
}> = ({ items, onChange }) => {
  /**
   *
   */
  const add = useCallback(() => {
    onChange([...items, '']);
  }, [items]);

  /**
   *
   */
  const remove = useCallback(
    (index: number) => {
      const filtertedItems = items.filter((_, idx) => idx !== index);
      onChange([...filtertedItems]);
    },
    [items]
  );

  /**
   *
   */
  const update = useCallback(
    (index: number, value: string) => {
      const current = items;
      current[index] = value;
      onChange([...current]);
    },
    [items]
  );

  return (
    <Stack direction={'column'} spacing={2}>
      <Divider textAlign="left">
        <Typography variant="inter12" fontWeight={'bold'}>
          DEPENDENCIES API PERMISSIONS
        </Typography>
      </Divider>

      {items.map((value, index) => (
        <Stack direction={'row'} key={index}>
          <TextField
            fullWidth
            className="background"
            variant="standard"
            value={value}
            onChange={e => update(index, e.target.value)}
            margin="dense"
            type="text"
          />

          <Button type="button" onClick={() => remove(index)} className="text-red-500">
            <DeleteOutlineIcon color="error" />
          </Button>
        </Stack>
      ))}

      <Button variant="outlined" type="button" onClick={add} className="text-blue-500 mt-2">
        + ADD DEPENDENCY
      </Button>
    </Stack>
  );
};

export default DialogForm;
